<template>
  <DSDialog
    :open
    @update:open="emit('update:open', $event)"
  >
    <DSDialogContent class="max-w-ds-dialog-md gap-0 p-0">
      <TagPicker
        :tags="catalogue"
        :selected
        :suggestions
        :loading
        :askable
        @toggle="emit('toggle', $event)"
        @create="emit('toggle', $event)"
        @ask="emit('ask')"
      >
        <template #heading>
          <DSDialogHeader class="p-0">
            <DSDialogTitle class="text-lg">
              Tags
            </DSDialogTitle>
            <DSDialogDescription class="sr-only">
              Search every tag in this repository, across any dimension, or
              create a new one.
            </DSDialogDescription>
          </DSDialogHeader>
        </template>

        <template #footer>
          <DSDialogFooter class="justify-between bg-background border-t border-border sm:justify-between">
            <p class="text-xs font-medium text-muted-foreground">
              Any dimension, not just asset class and strategy
            </p>
            <DSButton
              size="sm"
              @click="emit('update:open', false)"
            >
              Done
            </DSButton>
          </DSDialogFooter>
        </template>
      </TagPicker>
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { TagRef, TagSuggestion } from '@/entities/tags/utils/types'

import {
  DSButton,
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogFooter,
  DSDialogHeader,
  DSDialogTitle,
} from '@ds'
import { useTagsStore } from '@/store/tags'

import { commonTags } from '@/entities/tags/utils/helpers'

import TagPicker from '@/entities/tags/components/TagPicker.vue'

/*
 * The tag picker, nested inside the wizard rather than swapped in for it.
 *
 * TagPickerDialog is the modal-system adapter for the same TagPicker, and going
 * through the modal store would mean hiding this wizard to show the picker —
 * which unmounts it, taking the form state with it. Nested dialogs keep the
 * half-filled wizard alive underneath, which is the whole requirement.
 */
defineProps<{
  open: boolean
  selected: TagRef[]
  suggestions?: TagSuggestion[]
  /** Passed through to the picker's Suggested section — see TagPicker. */
  loading?: boolean
  /** Passed through to the picker's "Ask AI" row — see TagPicker. */
  askable?: boolean
}>()

/*
 * The catalogue is the repository's own, read here rather than passed in: every
 * caller of this adapter would otherwise have to fetch the same list to hand it
 * back. Wizard.vue is what makes sure it is loaded — see its onMounted.
 */
const tagsStore = useTagsStore()

/*
 * Without Category or Subcategory. "Any dimension" is the panel's promise and
 * those two are not the user's to file under — they are the app's category
 * tree. Narrowing the list narrows the create row's dimensions with it, since
 * TagPicker derives those from the tags it was given.
 */
const catalogue = computed(() => commonTags(tagsStore.getList))

const emit = defineEmits<{
  (e: 'update:open', open: boolean): void
  /** Toggle, not add: the picker shows applied tags as selected. */
  (e: 'toggle', tag: TagRef): void
  /** The "Ask AI" row was tapped — see TagPicker. */
  (e: 'ask'): void
}>()
</script>
